import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box } from '@mui/material';
import alertImage from 'assets/img/alert.svg';
import { CURRENCY_FORMATTER, PRODUCT_INVALID_VALUE } from 'common/constants';
import {
  ACTIVE_PRODUCT_MESSAGE,
  DEACTIVE_PRODUCT_MESSAGE,
} from 'common/constants/changeProductAvailabilityMessage.constants';
import { formatCurrency } from 'common/constants/currency';
import React, { Fragment, useState } from 'react';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { groupProductsHotUpdate, setGroupProductSelected } from 'v3/data/store/product/actions';
import { IGroupProduct } from 'v3/data/store/product/types';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { UnsupportedValueCard } from 'v3/pages/Products/List/styles';
import { updateGroupProduct } from 'v3/services/api';
import { ChangeValueModal } from 'v3/shared/components/Modal/ChangePriceModal';
import { ConfirmationModal } from 'v3/shared/components/Modal/ConfirmationModal';
import { SwitchCommon } from 'v3/shared/components/Switch';
import { LabelSmall } from 'v3/shared/typography/LabelSmall';
import {
  AlignClientInfo,
  AlignSwitch,
  AlignSwitchContainer,
  AlignValues,
  BodyLine,
  DeviceImg,
  HeaderLine,
  MainContainer,
  SubtitleLine,
  TableCellInRow,
} from '../styles';

interface ProductMobileTableForGroupProps {
  items: IGroupProduct[];
  shouldDisableAvailability: (product: IGroupProduct) => boolean;
}

export const ProductMobileTableForGroup: React.FC<ProductMobileTableForGroupProps> = ({
  items,
  shouldDisableAvailability,
}) => {
  const [changePriceModalOpen, setChangePriceModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const { storeUserId } = useStoreSelector((store) => store.store.storeSelected);
  const { groupProductSelected } = useStoreSelector((store) => store.products);

  const handleSwitchChange = (product: IGroupProduct, event: React.ChangeEvent<HTMLInputElement>) => {
    const switchNewValue = Boolean(event.target.checked);
    setGroupProductSelected({ ...product, availability: switchNewValue });
    const currentSubtitle = switchNewValue ? ACTIVE_PRODUCT_MESSAGE : DEACTIVE_PRODUCT_MESSAGE;
    setSubtitle(currentSubtitle);
    setConfirmationModalOpen(true);
  };

  const handleOpenEditPriceModal = (product: IGroupProduct) => {
    setGroupProductSelected(product);
    setChangePriceModalOpen(true);
  };

  const changePriceHandler = async (price: number) => {
    const newPrice = Number(price) / CURRENCY_FORMATTER;
    await updateGroupProduct(groupProductSelected.sku.id, {
      price: newPrice,
      storeUserId,
    });
    groupProductsHotUpdate({ ...groupProductSelected, price: newPrice });
  };

  const handleConfirm = async () => {
    try {
      await updateGroupProduct(groupProductSelected.sku.id, {
        availability: groupProductSelected.availability,
        storeUserId,
      });
      groupProductsHotUpdate(groupProductSelected);

      groupProductSelected.availability
        ? showSnackbar('O produto está ativo no catálogo', 'success', true)
        : showSnackbar('O produto foi desativado do catálogo', 'success', true);
    } catch (error) {
      showErrorDialog('Erro ao editar disponibilidade do produto.', error);
    } finally {
      setConfirmationModalOpen(false);
    }
  };

  return (
    <Fragment>
      {items.map((item) => (
        <MainContainer key={item.sku.id}>
          <AlignValues>
            <HeaderLine>Modelo</HeaderLine>
            <AlignClientInfo>
              <DeviceImg src={item.sku.image.marketingUrl} alt={item.sku.id} />
              <BodyLine>{item.sku.product.name}</BodyLine>
              <SubtitleLine>{`${item.sku.storage}GB`}</SubtitleLine>
            </AlignClientInfo>
          </AlignValues>

          <AlignValues>
            <HeaderLine>Preço</HeaderLine>
            <AlignClientInfo>
              <TableCellInRow>
                <Box>
                  <BodyLine>{formatCurrency(item.price)}</BodyLine>
                  {item.price <= PRODUCT_INVALID_VALUE && (
                    <UnsupportedValueCard>
                      <LabelSmall>Necessário informar o preço</LabelSmall>
                    </UnsupportedValueCard>
                  )}
                  {item.price > item.sku.maxPrice && (
                    <UnsupportedValueCard>
                      <LabelSmall>
                        Valor máximo do produto deve ser {formatCurrency(parseFloat(item.sku.maxPrice.toFixed(2)))}
                      </LabelSmall>
                    </UnsupportedValueCard>
                  )}
                </Box>

                <EditOutlinedIcon onClick={() => handleOpenEditPriceModal(item)} />
              </TableCellInRow>
            </AlignClientInfo>
          </AlignValues>

          <AlignSwitchContainer>
            <HeaderLine>Mostrar no Catálogo</HeaderLine>
            <AlignSwitch>
              <SwitchCommon
                disabled={shouldDisableAvailability(item)}
                checked={item.availability}
                onChange={(event) => {
                  handleSwitchChange(item, event);
                }}
              />
            </AlignSwitch>
          </AlignSwitchContainer>

          <ChangeValueModal
            modal={{
              initialPrice: groupProductSelected.price,
              active: changePriceModalOpen,
              title: 'Editar Preço',
              activeButton: true,
              cancelButton: 'Cancelar',
              saveButton: 'Salvar',
              maxPrice: groupProductSelected.sku.maxPrice,
            }}
            onClose={() => setChangePriceModalOpen(false)}
            onConfirm={changePriceHandler}
          />

          <ConfirmationModal
            modal={{
              active: confirmationModalOpen,
              title: 'Você tem certeza?',
              subtitle: subtitle,
              image: alertImage,
              cancelButtonText: 'Cancelar',
              confirmButtonText: 'Sim, desejo continuar',
            }}
            onClose={() => setConfirmationModalOpen(false)}
            onConfirm={handleConfirm}
          />
        </MainContainer>
      ))}
    </Fragment>
  );
};
